/**
 * @file The candidate picker of one part that resolution left ambiguous. Consumer:
 * `SignifyFetchJobPartDetailCell`.
 * It reads one key of the per-mount `picks` proxy, so choosing on one row repaints that row alone,
 * and writes the same key in its handler. Nothing is sent here: the footer's one button carries
 * every pick and every failed part into a single `retrySignifyFetchJob` call, which is also why the
 * picks die with the modal — an unsent pick has no meaning to the server.
 * The candidates come from the part's own document, so a part that stops being ambiguous stops
 * rendering this and its stale pick becomes invisible rather than wrong.
 * Clearing arrives as `undefined`, not `null`: rc-select's clear path calls `onChange` with the
 * first of an empty array. The key is DELETED rather than set, because the footer counts the picks
 * it will send by key and the callable refuses a pick carrying no code.
 */

import { FC } from 'react';
import { Select } from 'antd';
import { useSnapshot } from 'valtio';
import type { SignifyFetchJobPicks } from '../index.types';

type SignifyFetchJobCandidate = {
  code: string;
  name: string;
  commercialCode: string;
};

type SignifyFetchJobCandidatePickerProps = {
  partId: string;
  candidates: readonly SignifyFetchJobCandidate[];
  picks: SignifyFetchJobPicks;
};

const SignifyFetchJobCandidatePicker: FC<SignifyFetchJobCandidatePickerProps> = ({
  partId,
  candidates,
  picks,
}) => {
  const picksSnap = useSnapshot(picks);

  return (
    <Select
      size="small"
      showSearch={{ optionFilterProp: 'label' }}
      allowClear
      popupMatchSelectWidth={false}
      style={{ width: '100%' }}
      placeholder="Choose the product"
      aria-label={`signify-fetch-job-pick-${partId}`}
      value={picksSnap[partId] ?? null}
      options={candidates.map((candidate) => ({
        value: candidate.code,
        label: `${candidate.name} · ${candidate.commercialCode} · ${candidate.code}`,
      }))}
      onChange={(code: string | undefined) => {
        if (!code) {
          delete picks[partId];
          return;
        }
        picks[partId] = code;
      }}
    />
  );
};

export default SignifyFetchJobCandidatePicker;
